import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  getUserSettings,
  updateUserSettings,
  type UserSettings,
  type UserSettingsUpdate,
} from '../services/user-settings';

export const USER_SETTINGS_QUERY_KEY = ['user-settings'] as const;

export interface UseUserSettingsOptions {
  userId: string | null;
}

export interface UseUserSettingsResult {
  settings: UserSettings | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  updateSettings: (settings: UserSettingsUpdate) => Promise<UserSettings>;
  isUpdating: boolean;
}

/**
 * React Query hook for user settings with caching.
 * Uses stale-while-revalidate strategy for optimal UX.
 */
export function useUserSettings({ userId }: UseUserSettingsOptions): UseUserSettingsResult {
  const queryClient = useQueryClient();

  const {
    data: settings,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [...USER_SETTINGS_QUERY_KEY, userId],
    queryFn: () => getUserSettings(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
  });

  const mutation = useMutation({
    mutationFn: (newSettings: UserSettingsUpdate) => updateUserSettings(userId!, newSettings),
    // Optimistic: flip the cached value immediately so toggles feel instant, then
    // reconcile with the server (or roll back on error).
    onMutate: async (newSettings: UserSettingsUpdate) => {
      const key = [...USER_SETTINGS_QUERY_KEY, userId];
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<UserSettings>(key);
      if (previous) {
        queryClient.setQueryData<UserSettings>(key, { ...previous, ...newSettings });
      }
      return { previous };
    },
    onError: (_err, _newSettings, context) => {
      const ctx = context as { previous?: UserSettings } | undefined;
      if (ctx?.previous) {
        queryClient.setQueryData([...USER_SETTINGS_QUERY_KEY, userId], ctx.previous);
      }
    },
    onSuccess: (updatedSettings) => {
      // Reconcile with the authoritative server response.
      queryClient.setQueryData([...USER_SETTINGS_QUERY_KEY, userId], updatedSettings);
    },
  });

  return {
    settings,
    isLoading,
    isError,
    error: error as Error | null,
    updateSettings: mutation.mutateAsync,
    isUpdating: mutation.isPending,
  };
}

/**
 * Get cached user settings synchronously (for use in callbacks like connect).
 * Returns undefined if not cached.
 */
export function getCachedUserSettings(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string
): UserSettings | undefined {
  return queryClient.getQueryData([...USER_SETTINGS_QUERY_KEY, userId]);
}
